import { newRequestId } from '../../utils/api'
import { readLocalJson, readSession, writeLocal, writeLocalJson, writeSession } from '../../core/storage'
import type { AccountOrder } from '../../core/types/account'

/**
 * Wat er rond één bestelling in de browser bewaard blijft, op één plek:
 * - de poging-id (idempotentie over mounts heen);
 * - de bestelling die onderweg is naar Mollie (voor de terugkeerpagina);
 * - de bevestiging na betalen (blijft staan bij verversen).
 */

export type PlacedOrder = {
  orderId: string
  firstName: string
  email: string
  /** In euro's, alleen voor het tonen; komt uit `totalCents` van de server. */
  total: number
  gift: string | null
  /**
   * Het nette bestelnummer van de server; bestaat pas na betalen. Leeg (of ontbrekend in een
   * oudere bewaarde bevestiging) = "volgt in de bevestigingsmail", nooit het technische id.
   */
  orderNumber?: string | null
  /** Mag de bedankpagina "Hoe kwam je bij ons?" vragen (van de server)? Leeg = onbekend, wel vragen. */
  askSource?: boolean
  remind: boolean
  /** Voor "account maken na afrekenen": de bestelling komt meteen in het account. */
  accountOrder: AccountOrder
  subscribeIds: string[]
}

const isPlaced = (p: unknown): p is PlacedOrder => {
  const o = p as Partial<PlacedOrder> | null
  return Boolean(o && typeof o.orderId === 'string' && typeof o.total === 'number' && o.accountOrder)
}

// ——— De bevestiging (per tab) ———

const PLACED_KEY = 'bb-placed-order'

export function readPlaced(): PlacedOrder | null {
  const raw = readSession(PLACED_KEY)
  if (!raw) return null
  try {
    const p: unknown = JSON.parse(raw)
    return isPlaced(p) ? p : null
  } catch {
    return null
  }
}

export const savePlaced = (p: PlacedOrder) => writeSession(PLACED_KEY, JSON.stringify(p))

// ——— Onderweg naar Mollie ———
// In localStorage, niet per tab: een bank-app opent de terugkeerpagina soms in een nieuw tabblad.

const PENDING_KEY = 'bb-pending-order'
const PENDING_TTL_MS = 24 * 60 * 60 * 1000
type Pending = { order: PlacedOrder; at: number }

export const savePending = (order: PlacedOrder) => writeLocalJson(PENDING_KEY, { order, at: Date.now() } satisfies Pending)

/**
 * De bestelling die naar Mollie ging, als het om déze order gaat. Wist hem meteen: één keer
 * gebruiken. Een verlopen of onleesbare bevestiging (naam, e-mail, regels) gaat altijd weg, ook
 * als de klant via een andere bestelling terugkomt: persoonsgegevens blijven niet hangen.
 */
export function takePending(orderId: string): PlacedOrder | null {
  const stored = readLocalJson<Pending | null>(PENDING_KEY, null)
  if (!stored) return null
  const fresh = typeof stored.at === 'number' && Date.now() - stored.at < PENDING_TTL_MS
  if (!fresh || !isPlaced(stored.order)) {
    writeLocal(PENDING_KEY, '')
    return null
  }
  if (stored.order.orderId !== orderId) return null
  writeLocal(PENDING_KEY, '')
  return stored.order
}

/** Bij het openen van de winkel: een verlopen bevestiging opruimen, ook als ze nooit terugkomt. */
export function prunePending() {
  const stored = readLocalJson<Pending | null>(PENDING_KEY, null)
  if (stored && !(typeof stored.at === 'number' && Date.now() - stored.at < PENDING_TTL_MS && isPlaced(stored.order))) writeLocal(PENDING_KEY, '')
}

type CartLine = { productId: string; qty: number; subscribe: boolean }
const cartKey = (lines: readonly CartLine[]) =>
  lines
    .map((l) => `${l.productId}:${l.qty}:${l.subscribe ? 1 : 0}`)
    .sort()
    .join('|')

/**
 * Hoort de mand van nu nog bij deze bestelling? Alleen dan mag de terugkeerpagina hem wissen. Wie
 * tijdens het betalen in een ander tabblad een nieuwe mand begon, houdt die.
 */
export function cartBelongsTo(order: PlacedOrder, cart: readonly CartLine[]): boolean {
  const placed = order.accountOrder.lines.map((l) => ({ productId: l.productId, qty: l.qty, subscribe: order.subscribeIds.includes(l.productId) }))
  return cartKey(placed) === cartKey(cart)
}

// ——— De poging-id ———

/**
 * Idempotentie over mounts heen. De id hoort bij de volledige aanvraag (mand, adres, code,
 * notitie, betaalwijze, e-mail en totaal): precies hetzelfde = zelfde id (veilig opnieuw
 * proberen); iets gewijzigd = nieuwe id, anders geeft de server het antwoord op de óude
 * bestelling terug. Een poging verloopt na een dag, zodat een vaste klant die volgende
 * maand precies dezelfde mand bestelt nooit de order van vorige maand terugkrijgt.
 */
const ATTEMPT_KEY = 'bb-checkout-attempt'
const ATTEMPT_TTL_MS = 24 * 60 * 60 * 1000
type Attempt = { signature: string; id: string; at: number }

/**
 * Een korte vingerafdruk (FNV-1a, 2×32 bit) in plaats van de aanvraag zelf: zo staan adres en
 * e-mail niet een dag leesbaar in localStorage. Geen beveiliging, alleen "is dit dezelfde poging".
 */
export function fingerprint(text: string): string {
  let a = 0x811c9dc5
  let b = 0x01000193 ^ text.length
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i)
    a = Math.imul(a ^ c, 0x01000193)
    b = Math.imul(b ^ c, 0x5bd1e995)
  }
  return `${(a >>> 0).toString(16)}${(b >>> 0).toString(16)}`
}

export function attemptId(content: unknown): string {
  const signature = fingerprint(JSON.stringify(content))
  const stored = readLocalJson<Attempt | null>(ATTEMPT_KEY, null)
  const fresh = stored && typeof stored.at === 'number' && Date.now() - stored.at < ATTEMPT_TTL_MS
  if (fresh && stored.signature === signature && typeof stored.id === 'string') return stored.id
  const id = newRequestId()
  writeLocalJson(ATTEMPT_KEY, { signature, id, at: Date.now() } satisfies Attempt)
  return id
}

/** Na een bevestigde bestelling (of een afgewezen totaal): de volgende poging krijgt een nieuwe id. */
export const clearAttempt = () => writeLocal(ATTEMPT_KEY, '')
